import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { SpecularButton } from "./specular-button.js";
import { cn } from "../../lib/utils.js";

export interface ScrollSliderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export function ScrollSlider({ className, children, ...props }: ScrollSliderProps) {
  const trackRef = React.useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = React.useState(false);
  const [atStart, setAtStart] = React.useState(true);
  const [atEnd, setAtEnd] = React.useState(true);

  const measure = React.useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const overflow = el.scrollWidth > el.clientWidth + 1;
    setHasOverflow(overflow);
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  }, []);

  React.useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    el.addEventListener("scroll", measure, { passive: true });
    return () => { ro.disconnect(); el.removeEventListener("scroll", measure); };
  }, [measure, children]);

  function scrollByPage(direction: 1 | -1) {
    trackRef.current?.scrollBy({ left: direction * Math.round(trackRef.current.clientWidth * 0.8), behavior: "smooth" });
  }

  return (
    <div className="relative">
      {hasOverflow && (
        <SpecularButton
          size="sm" aria-label="Scroll left" disabled={atStart}
          className="absolute left-0 top-1/2 z-10 w-8 -translate-y-1/2 px-0"
          onClick={() => scrollByPage(-1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </SpecularButton>
      )}
      <div
        ref={trackRef}
        className={cn(
          "flex gap-3 overflow-x-auto scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          hasOverflow && "px-9",
          className,
        )}
        {...props}
      >
        {children}
      </div>
      {hasOverflow && (
        <SpecularButton
          size="sm" aria-label="Scroll right" disabled={atEnd}
          className="absolute right-0 top-1/2 z-10 w-8 -translate-y-1/2 px-0"
          onClick={() => scrollByPage(1)}
        >
          <ChevronRight className="h-4 w-4" />
        </SpecularButton>
      )}
    </div>
  );
}
